/* eslint-disable react/no-danger */
import { Alert, IconWithText, LinkButton } from '@pmi/dsm-react';
import {
  AlertInfoSolidIcon,
  AlertSuccessOutlineIcon,
  DiscountOutlineIcon,
} from '@pmi/dsm-react/dist/components/icons';
import { PlusIcon } from '@pmi/dsm-react/dist/components/icons/16';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { reportLinkClick, toastWithAnalytics } from '../../../shared/analytics';
import CategoryLabel from '../../../shared/components/CategoryLabel';
import {
  DisabledReason,
  isPayByUserCartStatus,
  PAY_BY_USER_STATUS_MAP,
  ProductTypes,
} from '../../../shared/constants';
import { extractTextFromHtml } from '../../../shared/helpers';
import { adminInfoSelector } from '../../../shared/store/shared/adminInfoSlice';
import { openCart } from '../../../shared/store/shared/cartSlice';
import { useGetCartDetailsQuery } from '../../../shared/components/Cart/apiSlice';
import { B2CPRODUCTLOCATION } from '../../../shared/utils/defaultTexts';
import CertProductStatus from '../CertProductStatus';
import DetailModal from '../DetailModal';
import { Product } from '../models';
import styles from '../productcard.module.scss';
import { AddToCartItem, useAddToCartMutation } from './apiSlice';
import BundleOptionSelector, {
  BundleOptionSelections,
} from './BundleOptionSelector';
import ProductModal from './ProductModal';
import {
  getSelectableBundleOptions,
  isBundleOptionSelectable,
} from './ProductModal/EnhancedEnrollmentModal/helpers';

/**
 * Props for the EnrollmentCard component.
 */
interface EnrollmentCardProps {
  product: Product; // The product details to display in the card
  accountId: string; // The account ID of the user
  onSuccess: (product?: Product | undefined) => void; // Callback for successful enrollment
  isFiltered: boolean; // Indicates if the product is filtered
}

/**
 * EnrollmentCard component.
 *
 * This component renders a card for displaying product details and handling user interactions
 * such as enrollment or adding the product to the cart.
 *
 * @param {EnrollmentCardProps} props - The props for the component.
 * @returns {JSX.Element} The rendered component.
 */
export default function EnrollmentCard({
  product,
  onSuccess,
  accountId,
  isFiltered,
}: EnrollmentCardProps) {
  const {
    name,
    level,
    shortDescription = '',
    type,
    callToAction,
    disabledReasonMessage,
    status,
    submitted,
  } = product;

  // State for managing modal visibility and cart actions
  const [productModalState, setProductModalState] = useState(false);
  const [detailModalState, setDetailModalState] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [bundleOptionSelections, setBundleOptionSelections] =
    useState<BundleOptionSelections>({});
  const [showSelectionErrors, setShowSelectionErrors] = useState(false);

  // Selectable bundle options (e.g. language) require a user selection before add to cart.
  const selectableBundleOptions = useMemo(
    () => getSelectableBundleOptions(product.bundleOptions),
    [product.bundleOptions],
  );
  const requiresSelection =
    product.isPayByUserProduct && selectableBundleOptions.length > 0;
  const allSelectionsMade = selectableBundleOptions.every(
    (bo) => bundleOptionSelections[String(bo.id)],
  );

  // Redux selectors and dispatch
  const { adminDetails } = useSelector(adminInfoSelector);
  const [addToCart, addedToCart] = useAddToCartMutation();
  const dispatch = useDispatch();

  const isInCart = !!callToAction?.text
    ?.toLowerCase()
    .trim()
    .includes(PAY_BY_USER_STATUS_MAP.inCart);

  // Pull cart details so we can preselect the option(s) for products already in cart.
  const { data: cartDetails } = useGetCartDetailsQuery(
    { accountId },
    { skip: !accountId || !requiresSelection || !isInCart },
  );

  const cartItemForProduct = useMemo(
    () => cartDetails?.items?.find((item) => item.sku === product.sku),
    [cartDetails, product.sku],
  );

  // When the product is already in the cart, mirror the cart's selection into local state
  // so the dropdown(s) display the chosen value and stay disabled.
  useEffect(() => {
    if (!requiresSelection || !isInCart || !cartItemForProduct?.bundleOptions) {
      return;
    }
    const next: BundleOptionSelections = {};
    selectableBundleOptions.forEach((option) => {
      const cartOption = cartItemForProduct.bundleOptions.find(
        (bo) => bo.optionId === option.id,
      );
      if (!cartOption) return;
      const matchingLink = option.productLinks.find(
        (link) => link.sku === cartOption.selectionSku,
      );
      if (matchingLink) {
        next[String(option.id)] = matchingLink.sku;
      }
    });
    if (Object.keys(next).length > 0) {
      setBundleOptionSelections((prev) => ({ ...prev, ...next }));
    }
  }, [
    requiresSelection,
    isInCart,
    cartItemForProduct,
    selectableBundleOptions,
  ]);

  /**
   * Handles successful enrollment.
   */
  const onEnrollmentSuccess = () => {
    setProductModalState(false);
    onSuccess();
  };

  /**
   * Handles modal cancellation.
   */
  const onCancel = () => {
    setProductModalState(false);
  };

  /**
   * Handles click on the call-to-action button.
   */
  const onCtaClick = () => {
    if (callToAction?.url) {
      window.location.href = callToAction.url;
      return;
    }
    setProductModalState(true);
  };

  /**
   * Handles adding the product to the cart.
   */
  const onAddToCart = () => {
    const bundleOptions = (): { optionId: number; selectionId: number }[] => {
      const options: { optionId: number; selectionId: number }[] = [];
      if (product.bundleOptions && product.bundleOptions.length > 0) {
        product.bundleOptions.map((option) => {
          if (option.productLinks.length > 0) {
            if (isBundleOptionSelectable(option)) {
              const selectedSku = bundleOptionSelections[String(option.id)];
              const selectedLink = option.productLinks.find(
                (link) => link.sku === selectedSku,
              );
              if (selectedLink) {
                options.push({
                  optionId: option.id,
                  selectionId: Number(selectedLink.id),
                });
              }
            } else if (option.productLinks.length > 1) {
              option.productLinks.map((link) => {
                options.push({
                  optionId: option.id,
                  selectionId: Number(link.id),
                });
                return link;
              });
            } else {
              options.push({
                optionId: option.id,
                selectionId: Number(option.productLinks[0].id),
              });
            }
          }
          return option;
        });
      }
      return options;
    };
    const item: AddToCartItem = {
      qty: 1,
      sku: product.sku,
      dealCode: product.currentDealId,
      price: product.price,
      bundleOptions: bundleOptions(),
    };
    setAddingToCart(true);
    addToCart({
      accountId,
      items: [item],
    })
      .unwrap()
      .then(() => {
        addedToCart.reset();
        dispatch(openCart());
      })
      .catch((err) => {
        if (err?.data && err?.data?.Value) {
          const val = JSON.parse(err.data.Value);
          if (val?.detail) {
            const details = JSON.parse(val.detail);
            let msg = details.message.replaceAll('qty', 'quantity');
            if (
              details?.parameters &&
              Array.isArray(details?.parameters) &&
              details.parameters.length > 0
            ) {
              details.parameters.map((v: string, i: number) => {
                msg = msg.replace(`%${i + 1}`, v);
                return v;
              });
              toastWithAnalytics(msg, 'danger');
            } else {
              toastWithAnalytics(msg, 'danger');
            }
          }
        }
      })
      .finally(() => {
        setAddingToCart(false);
      });
  };

  /**
   * Determines if the "Pay By User" call-to-action should be shown.
   * @returns {boolean} True if the "Pay By User" CTA should be shown, false otherwise.
   */
  const showPayByUserCta = () =>
    isPayByUserCartStatus(product.callToAction?.text) &&
    product.isPayByUserProduct &&
    product.price > 0;

  /**
   * Renders the enrollment call-to-action button.
   * @returns {JSX.Element | null} The rendered button or null.
   */
  const enrollmentCta = () =>
    callToAction &&
    (callToAction.text || callToAction.url) && (
      <div
        className="mt-4"
        data-type={product.type.toLowerCase()}
        data-sku={product.sku}
      >
        <LinkButton
          variant="outline-secondary"
          onClick={() => {
            reportLinkClick(
              `${name}:${callToAction?.text}`,
              callToAction?.url,
              'b2bhub_allproducts',
              isFiltered,
            );
            onCtaClick();
          }}
          titleText={callToAction?.text}
          fullWidth={false}
          data-testid="cta-button"
          className="btn-card-cta"
        />
      </div>
    );

  /**
   * Renders the "Pay By User" call-to-action button.
   * @returns {JSX.Element | null} The rendered button or null.
   */
  const payByUserCta = () =>
    callToAction && (
      <div
        className="mt-auto"
        data-type={product.type.toLowerCase()}
        data-sku={product.sku}
      >
        <LinkButton
          variant="outline-secondary"
          className={`${styles.btnCTAction} btn-pbu`}
          onClick={() => {
            if (isInCart || addingToCart) {
              return;
            }
            if (requiresSelection && !allSelectionsMade) {
              setShowSelectionErrors(true);
              return;
            }
            reportLinkClick(
              `${name}:${callToAction?.text}`,
              callToAction?.url,
              'b2bhub_allproducts',
              isFiltered,
            );
            if (
              product.type === ProductTypes.membership &&
              product.bundleProduct &&
              product.bundleOptions.length > 0
            ) {
              const chapterBundleOptions = product.bundleOptions.filter((bo) =>
                bo.title.toLowerCase().includes('chapter'),
              );
              if (chapterBundleOptions.length > 0) {
                setProductModalState(true);
                return;
              }
            }
            onAddToCart();
          }}
          titleText={callToAction?.text}
          fullWidth={false}
          data-testid="cta-button"
          disabled={addingToCart || isInCart}
          icon={
            !isInCart ||
            callToAction?.text.toLowerCase().includes('add to cart')
              ? PlusIcon
              : null
          }
        />
      </div>
    );

  /**
   * Renders the "Pay By User" content, including pricing and discount details.
   * @returns {JSX.Element} The rendered content.
   */
  const payByUserContent = () => {
    const discountLabel = () => {
      let text = '';

      if (product.price < product.msrpPrice) {
        if (
          (product.isPmiMember || product.hasMembershipInCart) &&
          product.discountPercent <= 0
        ) {
          text = `Price includes Member discount`;
        } else if (
          (product.isPmiMember || product.hasMembershipInCart) &&
          product.discountPercent > 0
        ) {
          text = `Price includes Member + ${product.discountPercent}% company discount`;
        } else if (product.discountPercent > 0) {
          text = `Price includes ${product.discountPercent}% company discount`;
        }
      }

      if (text.length === 0) {
        return null;
      }

      return (
        <p className={`${styles.payByUserDiscount} mb-2`}>
          <IconWithText
            text={text}
            icon={<DiscountOutlineIcon />}
            className="d-none d-sm-flex"
          />
          <div className="d-flex d-sm-none align-items-start mb-3">
            <p>
              <AlertSuccessOutlineIcon
                fontSize="sm"
                className="d-inline-block float-start"
              />{' '}
              <span className="d-inline">{text}</span>
            </p>
          </div>
        </p>
      );
    };

    return (
      <div className={styles.payByUser}>
        {requiresSelection && (
          <BundleOptionSelector
            selectableBundleOptions={selectableBundleOptions}
            selections={bundleOptionSelections}
            onChange={(next) => {
              setBundleOptionSelections(next);
              if (showSelectionErrors) {
                setShowSelectionErrors(false);
              }
            }}
            showErrors={showSelectionErrors && !isInCart}
            disabled={isInCart}
          />
        )}
        {product.price > 0 && (
          <>
            <hr className={styles.hSep} />
            {discountLabel()}
            <h3 className={`${styles.payByUserPrice} mb-4`}>
              {product.currencySymbol}
              {product.price.toFixed(2)}
              {product.price < product.msrpPrice && product.msrpPrice > 0 && (
                <span style={{ textDecoration: 'line-through' }}>
                  {product.currencySymbol}
                  {product.msrpPrice.toFixed(2)}
                </span>
              )}
            </h3>
            {!showPayByUserCta() &&
              product.type === ProductTypes.certification && (
                <em className="label">
                  Payment is due after application is processed.
                </em>
              )}
          </>
        )}
        {showPayByUserCta() && payByUserCta()}
        {!showPayByUserCta() && enrollmentCta()}
      </div>
    );
  };

  return (
    <>
      <div
        className="d-grid col-md-6 col-12"
        id={`pcard-${product.sku.toLowerCase()}`}
      >
        <div className="card-container d-grid mb-4 me-0">
          <div
            className={`card d-grid p-2 p-lg-3 ${styles.productCard} bg-white overflow-hidden w-100 d-block`}
          >
            <div className="d-flex flex-column p-1 p-lg-2 overflow-hidden">
              <div>
                {!product?.bundleOptions?.some((x) =>
                  x.title
                    ?.toLowerCase()
                    .includes(ProductTypes.membership.toLowerCase()),
                ) &&
                  product?.bundleProduct && (
                    <CategoryLabel
                      type="Bundle"
                      className="mb-3 me-2 float-start"
                    />
                  )}
                <CategoryLabel type={type} className="mb-3 float-start" />
              </div>
              <h4
                className="d-inline-block"
                dangerouslySetInnerHTML={{ __html: name }}
              />
              <div className="flex-grow-1">
                <span
                  data-testid="description"
                  dangerouslySetInnerHTML={{
                    __html:
                      shortDescription.length > 0
                        ? `${extractTextFromHtml(
                            shortDescription.substring(0, 97),
                          )}...`
                        : extractTextFromHtml(shortDescription),
                  }}
                />
                <LinkButton
                  data-testid="more-details-link"
                  titleText="More Details"
                  size="xs"
                  className="d-inline fw-light card-more-details"
                  onClick={() => {
                    reportLinkClick(
                      `${name}:more details`,
                      '',
                      'b2bhub_allproducts',
                      isFiltered,
                    );
                    setDetailModalState(true);
                  }}
                />
              </div>
              {level && (
                <div className="label my-2">
                  <div className="mb-1 mb-md-3">
                    <span data-testid="course-level">{level}</span>
                  </div>
                </div>
              )}
              {type === ProductTypes.certification && (
                <CertProductStatus
                  accountId={accountId}
                  status={status}
                  submitted={submitted}
                />
              )}
              {disabledReasonMessage && (
                <div className="label mt-auto">
                  <Alert
                    variant="info"
                    alertText={disabledReasonMessage
                      .replace('%adminDetails%', adminDetails)
                      .replace(
                        '%b2cProductLocation%',
                        B2CPRODUCTLOCATION[product.type]?.prefix,
                      )
                      .replace(/\(,\s/, '(')}
                    icon={AlertInfoSolidIcon}
                    actionableLinkHref={
                      product.disabledReason === DisabledReason.B2CPurchase &&
                      B2CPRODUCTLOCATION[product.type]?.link
                    }
                    actionableLinkText={
                      product.disabledReason === DisabledReason.B2CPurchase &&
                      B2CPRODUCTLOCATION[product.type]?.title
                    }
                  />
                </div>
              )}
              {product.isPayByUserProduct
                ? payByUserContent()
                : enrollmentCta()}
            </div>
          </div>
        </div>
      </div>
      {productModalState && (
        <ProductModal
          accountId={accountId}
          product={product}
          onCancel={(report: boolean = true) => {
            if (report)
              reportLinkClick(
                `${product.name}:Cancel`,
                '',
                'b2bhub_allproducts',
                isFiltered,
              );
            onCancel();
          }}
          onSubmit={() => {
            reportLinkClick(
              `${name}:Confirm and Proceed`,
              product.callToAction?.url,
              'b2bhub_allproducts',
              isFiltered,
            );
          }}
          onSuccess={onEnrollmentSuccess}
        />
      )}
      {detailModalState && (
        <DetailModal
          product={product}
          onCancel={(report: boolean = true) => {
            if (report)
              reportLinkClick(
                `${product.name}:Cancel`,
                '',
                'b2bhub_allproducts',
                isFiltered,
              );
            setDetailModalState(false);
          }}
          onContinue={() => {
            reportLinkClick(
              `${name}:${product.callToAction?.text}`,
              product.callToAction?.url,
              'b2bhub_allproducts',
              isFiltered,
            );

            if (
              product.type === 'Membership' &&
              product.bundleProduct &&
              product.bundleOptions.find((b) =>
                b.title.toLowerCase().includes('chapter'),
              )
            ) {
              onCtaClick();
              return;
            }
            const btn: HTMLAnchorElement | null = document.querySelector(
              `[data-sku="${product.sku}"] a`,
            );
            if (btn) {
              btn.click();
            }
            setTimeout(() => {
              setDetailModalState(false);
            }, 300);
          }}
        />
      )}
    </>
  );
}
